/**
 * Smart Lead Assignment Component
 * AI-powered lead assignment recommendations
 * Phase 6.5 - AI Management Tools
 */

import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Avatar,
  Button,
  Grid,
  LinearProgress,
  Skeleton,
  Divider
} from '@mui/material';
import {
  Psychology as AIIcon,
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  TrendingUp as MatchIcon,
  Star as RatingIcon,
  Schedule as AvailabilityIcon,
  CheckCircle as SuccessIcon
} from '@mui/icons-material';

export interface LeadAssignmentSuggestion {
  leadId: string;
  leadName: string;
  leadIndustry: string;
  leadValue: number;
  aiScore: number;
  topMatches: {
    salesRepId: string;
    salesRepName: string;
    matchScore: number;
    reasons: string[];
    availability: 'available' | 'busy' | 'full';
    currentLeads: number;
    maxCapacity: number;
  }[];
}

export interface SmartLeadAssignmentProps {
  suggestions: LeadAssignmentSuggestion[];
  loading?: boolean;
  onAssign?: (leadId: string, salesRepId: string) => void;
}

const SmartLeadAssignment: React.FC<SmartLeadAssignmentProps> = ({
  suggestions,
  loading = false,
  onAssign
}) => {
  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-HK', {
      style: 'currency',
      currency: 'HKD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Get availability color
  const getAvailabilityColor = (availability: string) => {
    switch (availability) {
      case 'available':
        return 'success';
      case 'busy':
        return 'warning';
      case 'full':
        return 'error';
      default:
        return 'default';
    }
  };

  // Get match score color
  const getMatchScoreColor = (score: number) => {
    if (score >= 90) return 'success.main';
    if (score >= 75) return 'info.main';
    if (score >= 60) return 'warning.main';
    return 'text.secondary';
  };

  if (loading) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <AssignmentIcon color="primary" />
          <Typography variant="h6" fontWeight="600">
            Smart Lead Assignment
          </Typography>
        </Box>
        {[1, 2].map((i) => (
          <Skeleton key={i} variant="rectangular" height={200} sx={{ mb: 2, borderRadius: 1 }} />
        ))}
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Avatar sx={{ bgcolor: 'info.main', width: 36, height: 36 }}>
          <AssignmentIcon sx={{ fontSize: 20 }} />
        </Avatar>
        <Box>
          <Typography variant="h6" fontWeight="600">
            Smart Lead Assignment
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {suggestions.length} leads pending assignment
          </Typography>
        </Box>
      </Box>

      {/* Suggestions List */}
      {suggestions.map((suggestion, index) => (
        <Paper
          key={suggestion.leadId}
          sx={{
            mb: 2,
            p: 2,
            border: 1,
            borderColor: 'divider',
            '&:hover': { boxShadow: 3 }
          }}
        >
          {/* Lead Info */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle1" fontWeight="600">
                  {suggestion.leadName}
                </Typography>
                <Chip
                  label={suggestion.leadIndustry}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  icon={<RatingIcon />}
                  label={`AI Score: ${suggestion.aiScore}`}
                  size="small"
                  color="primary"
                  sx={{ fontWeight: 600 }}
                />
                <Chip
                  label={formatCurrency(suggestion.leadValue)}
                  size="small"
                  color="success"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                p: 1,
                bgcolor: 'info.50',
                borderRadius: 1
              }}
            >
              <AIIcon sx={{ fontSize: 16, color: 'info.main' }} />
              <Typography variant="caption" color="info.main">
                AI has analyzed {suggestion.topMatches.length} sales reps and found the best matches
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Top Matches */}
          <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 1.5 }}>
            Recommended Assignments (Ranked by AI)
          </Typography>

          <Grid container spacing={2}>
            {suggestion.topMatches.slice(0, 3).map((match, idx) => {
              const capacityPercentage = (match.currentLeads / match.maxCapacity) * 100;

              return (
                <Grid item xs={12} key={match.salesRepId}>
                  <Paper
                    sx={{
                      p: 1.5,
                      bgcolor: idx === 0 ? 'success.50' : 'grey.50',
                      border: 1,
                      borderColor: idx === 0 ? 'success.main' : 'divider',
                      position: 'relative'
                    }}
                  >
                    {idx === 0 && (
                      <Chip
                        label="BEST MATCH"
                        size="small"
                        color="success"
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          height: 18,
                          fontSize: '0.65rem',
                          fontWeight: 700
                        }}
                      />
                    )}

                    {/* Rep Info */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                      <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}>
                        {match.salesRepName.charAt(0)}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="body2" fontWeight="600">
                            {match.salesRepName}
                          </Typography>
                          <Chip
                            label={match.availability.toUpperCase()}
                            size="small"
                            color={getAvailabilityColor(match.availability)}
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        </Box>

                        {/* Match Score */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <MatchIcon sx={{ fontSize: 16, color: getMatchScoreColor(match.matchScore) }} />
                          <Typography
                            variant="caption"
                            fontWeight={600}
                            sx={{ color: getMatchScoreColor(match.matchScore) }}
                          >
                            {match.matchScore}% Match Score
                          </Typography>
                        </Box>

                        {/* Capacity */}
                        <Box sx={{ mb: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              Capacity
                            </Typography>
                            <Typography variant="caption" fontWeight="600">
                              {match.currentLeads} / {match.maxCapacity} leads
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={capacityPercentage}
                            color={capacityPercentage >= 90 ? 'error' : capacityPercentage >= 70 ? 'warning' : 'success'}
                            sx={{ height: 4, borderRadius: 2 }}
                          />
                        </Box>

                        {/* Reasons */}
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            Why this match:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {match.reasons.map((reason, rIdx) => (
                              <Chip
                                key={rIdx}
                                label={reason}
                                size="small"
                                variant="outlined"
                                sx={{ height: 18, fontSize: '0.65rem' }}
                              />
                            ))}
                          </Box>
                        </Box>
                      </Box>
                    </Box>

                    {/* Assign Button */}
                    <Button
                      variant={idx === 0 ? 'contained' : 'outlined'}
                      size="small"
                      fullWidth
                      startIcon={idx === 0 ? <SuccessIcon /> : <AssignmentIcon />}
                      onClick={() => onAssign?.(suggestion.leadId, match.salesRepId)}
                      disabled={match.availability === 'full'}
                    >
                      {idx === 0 ? 'Assign to Best Match' : 'Assign'}
                    </Button>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>

          {/* View All Button */}
          {suggestion.topMatches.length > 3 && (
            <Button size="small" fullWidth sx={{ mt: 1 }}>
              View All {suggestion.topMatches.length} Matches
            </Button>
          )}
        </Paper>
      ))}

      {/* Empty State */}
      {suggestions.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
          <AssignmentIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
          <Typography variant="body2">
            No leads pending assignment. All leads have been assigned!
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SmartLeadAssignment;
